import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type {
  JourneyDetail,
  JourneyTaskRow,
  JourneyTaskUpdateInput,
} from "./types";

export type UpdateTaskInput = {
  journeyId: string;
  taskId: string;
  patch: JourneyTaskUpdateInput;
};

type Context = {
  previousDetail: JourneyDetail | undefined;
};

// PATCH /api/admin/journeys/:journeyId/tasks/:taskId
//
// Replaces /app/admin/journeys/_actions.ts::updateJourneyTaskAction.
// Pattern B field-level optimistic — merges `patch` into the matching task in
// `detail.tasks`; rolls back on error; invalidates on settled. Mirrors
// bucket-5 use-update-field.
export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation<{ ok: true }, ApiClientError, UpdateTaskInput, Context>({
    mutationFn: ({ journeyId, taskId, patch }) =>
      apiFetch<{ ok: true }>(
        `/admin/journeys/${journeyId}/tasks/${taskId}`,
        {
          method: "PATCH",
          body: JSON.stringify(patch),
        },
      ),
    onMutate: async ({ journeyId, taskId, patch }) => {
      const detailKey = queryKeys.journeys.detail(journeyId);
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previousDetail = queryClient.getQueryData<JourneyDetail>(detailKey);
      queryClient.setQueryData<JourneyDetail>(detailKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          tasks: old.tasks.map((t): JourneyTaskRow =>
            t.id === taskId ? { ...t, ...patch } : t,
          ),
        };
      });
      return { previousDetail };
    },
    onError: (err, { journeyId }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          queryKeys.journeys.detail(journeyId),
          context.previousDetail,
        );
      }
      toast.error(err.message ?? "Failed to update task");
    },
    onSettled: (_data, _err, { journeyId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.journeys.detail(journeyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.journeys.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.portal.tasks.all });
    },
  });
}
